import React, { createContext, useContext, useMemo } from 'react'
import PropTypes from 'prop-types'

// Pass-through context exposing the slice of `PortfolioExplorerContent`
// state utility-panel tools commonly need. The state itself still lives
// in `usePortfolioExplorerQuery`, `useExplorerConfig`, and
// `usePortfolioExplorerPreferences` -- this provider just publishes the
// already-derived values so a tool component doesn't have to drill them
// in via the panel's `payload`, which is reserved for the imperative
// "what did the user click?" trigger context.
//
// Intentionally lean: only fields that today's allocation tool needs
// (config + identity + columns + scoping flags + the live entity from
// useOrionIdentifier). Add new fields here as additional tools land
// rather than letting every tool re-derive identity / formatting / etc.
//
// Notable omissions: `rows` / `totalRow` / `expandToLevelLazy` are
// deliberately NOT exposed. The allocation tool fires its own Orion
// request (a "virtual grouping" -- single-level config) so totals foot
// with the household by construction, without needing the grid's
// in-progress lazy-expanded row tree.

const PortfolioDataContext = createContext(null)

export function PortfolioDataProvider ({ value, children }) {
  // Memoize the published value so consumers don't re-render on every
  // parent render (the parent's other state updates would otherwise
  // cascade through every tool component subscribing to this context).
  // Build a fresh object from the destructured subfields rather than
  // returning the parent's `value` reference -- this satisfies
  // react-hooks/exhaustive-deps (which can't see that `value`'s
  // subfields ARE its dependencies) AND gives consumers a reference
  // that's stable across parent re-renders that don't change any of
  // the listed subfields.
  const {
    config,
    asOfDate,
    scopeFiltersEnabled,
    columnDefinitions,
    groupings,
    prefs,
    clientId,
    entity,
    entityIds,
    orionRequest,
    addDefaultChart,
    removeDefaultChart,
    updateDefaultChart,
    setDefaultCharts,
    selectedSubject,
    setSubjectRow,
    setSubjectLevel,
    toggleSubjectLevel,
    clearSelectedSubject
  } = value
  const stable = useMemo(() => ({
    config,
    asOfDate,
    scopeFiltersEnabled,
    columnDefinitions,
    groupings,
    prefs,
    clientId,
    entity,
    entityIds,
    orionRequest,
    addDefaultChart,
    removeDefaultChart,
    updateDefaultChart,
    setDefaultCharts,
    selectedSubject,
    setSubjectRow,
    setSubjectLevel,
    toggleSubjectLevel,
    clearSelectedSubject
  }), [
    config,
    asOfDate,
    scopeFiltersEnabled,
    columnDefinitions,
    groupings,
    prefs,
    clientId,
    entity,
    entityIds,
    orionRequest,
    addDefaultChart,
    removeDefaultChart,
    updateDefaultChart,
    setDefaultCharts,
    selectedSubject,
    setSubjectRow,
    setSubjectLevel,
    toggleSubjectLevel,
    clearSelectedSubject
  ])

  return (
    <PortfolioDataContext.Provider value={stable}>
      {children}
    </PortfolioDataContext.Provider>
  )
}

PortfolioDataProvider.propTypes = {
  value: PropTypes.shape({
    config: PropTypes.object.isRequired,
    asOfDate: PropTypes.string,
    scopeFiltersEnabled: PropTypes.bool,
    columnDefinitions: PropTypes.array,
    groupings: PropTypes.arrayOf(PropTypes.string),
    prefs: PropTypes.object,
    clientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    entity: PropTypes.string,
    entityIds: PropTypes.array,
    orionRequest: PropTypes.object,
    addDefaultChart: PropTypes.func,
    removeDefaultChart: PropTypes.func,
    updateDefaultChart: PropTypes.func,
    setDefaultCharts: PropTypes.func,
    selectedSubject: PropTypes.shape({
      kind: PropTypes.oneOf(['row', 'level']).isRequired,
      row: PropTypes.object,
      depth: PropTypes.number,
      label: PropTypes.string
    }),
    setSubjectRow: PropTypes.func,
    setSubjectLevel: PropTypes.func,
    toggleSubjectLevel: PropTypes.func,
    clearSelectedSubject: PropTypes.func
  }).isRequired,
  children: PropTypes.node
}

export function usePortfolioData () {
  const ctx = useContext(PortfolioDataContext)
  if (!ctx) {
    throw new Error('usePortfolioData must be used within a PortfolioDataProvider')
  }
  return ctx
}
